import { useEffect, useRef, useState } from "react";
import { fetchChats, sendText } from "../../api/whatsapp.js";
import type { Lead, TextShortcut, WhatsappChat } from "../../types.ts";
import { shouldRefreshChatsAfterSend } from "../Chat.helpers.ts";
import { expandShortcutText, findShortcutByTrigger } from "../Shortcuts.helpers.ts";
import { AttachmentMenu } from "./composer/AttachmentMenu.tsx";
import { ComposerTextInput } from "./composer/ComposerTextInput.tsx";
import { EmojiPicker } from "./composer/EmojiPicker.tsx";
import { RecordingComposer } from "./composer/RecordingComposer.tsx";
import { ShortcutPicker } from "./composer/ShortcutPicker.tsx";

type ComposerProps = {
  chat: WhatsappChat;
  lead: Lead | null;
  shortcuts: TextShortcut[];
  text: string;
  recording: boolean;
  onLoadShortcuts: () => Promise<TextShortcut[]>;
  onTextChange: (text: string) => void;
  onClearText: () => void;
  onRestoreText: (text: string) => void;
  onRecordingChange: (recording: boolean) => void;
  onChatResolved?: (chat: WhatsappChat, previousChatJid: string) => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
};

type ComposerPopover = "attach" | "emoji" | "shortcut" | null;

export function Composer({
  chat,
  lead,
  shortcuts,
  text,
  recording,
  onLoadShortcuts,
  onTextChange,
  onClearText,
  onRestoreText,
  onRecordingChange,
  onChatResolved,
  onError,
  onSuccess,
}: ComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const latestTextRef = useRef(text);
  const [activePopover, setActivePopover] = useState<ComposerPopover>(null);

  function refreshAfterSend() {
    if (shouldRefreshChatsAfterSend(chat)) {
      fetchChats({ limit: 2000 }).catch(() => undefined);
    }
  }

  function focusComposer(caret = latestTextRef.current.length) {
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      try { el.setSelectionRange(caret, caret); } catch { /* ignore */ }
    });
  }

  function applyComposerText(value: string) {
    latestTextRef.current = value;
    onTextChange(value);
    focusComposer(value.length);
  }

  function insertEmoji(emoji: string) {
    const node = textareaRef.current;
    const current = latestTextRef.current || "";
    let next: string;
    let nextCaret: number;
    if (node && document.activeElement === node) {
      const start = node.selectionStart ?? current.length;
      const end = node.selectionEnd ?? current.length;
      next = current.slice(0, start) + emoji + current.slice(end);
      nextCaret = start + emoji.length;
    } else {
      next = current + emoji;
      nextCaret = next.length;
    }
    latestTextRef.current = next;
    onTextChange(next);
    focusComposer(nextCaret);
  }

  useEffect(() => {
    latestTextRef.current = text;
  }, [text]);

  function togglePopover(popover: Exclude<ComposerPopover, null>) {
    setActivePopover((current) => (current === popover ? null : popover));
  }

  function closePopover(popover: Exclude<ComposerPopover, null>) {
    setActivePopover((current) => (current === popover ? null : current));
  }

  function handleTextChange(value: string) {
    latestTextRef.current = value;
    onTextChange(value);
    void expandTypedShortcut(value);
  }

  async function expandTypedShortcut(value: string) {
    const typed = value.trim();
    if (!typed || !typed.startsWith("/") || /\s/.test(typed)) return;
    let shortcut = findShortcutByTrigger(shortcuts, typed);
    if (!shortcut && !shortcuts.length) {
      try {
        const loaded = await onLoadShortcuts();
        if (latestTextRef.current.trim() !== typed) return;
        shortcut = findShortcutByTrigger(loaded, typed);
      } catch (err) {
        onError(err instanceof Error ? err.message : "Falha ao carregar atalhos");
        return;
      }
    }
    if (!shortcut) return;
    const expanded = expandShortcutText(shortcut.body, { lead, chat });
    if (!expanded || latestTextRef.current.trim() !== typed) return;
    latestTextRef.current = expanded;
    onTextChange(expanded);
    focusComposer(expanded.length);
    onSuccess(`Atalho ${shortcut.trigger} inserido`);
  }

  async function submitText() {
    const value = text.trim();
    if (!value) return;
    onClearText();
    try {
      const result = await sendText(chat.chatJid, value, {
        accountSessionId: chat.accountSessionId || "",
        leadId: lead?.id ?? chat.leadId ?? "",
        phone: lead?.phone || chat.phone || "",
        displayName: lead?.name || chat.pushName || chat.subject || "",
      });
      const resolvedChat = (result as { chat?: WhatsappChat } | null | undefined)?.chat;
      if (resolvedChat?.chatJid && resolvedChat.chatJid !== chat.chatJid) {
        onChatResolved?.(resolvedChat, chat.chatJid);
      }
      refreshAfterSend();
    } catch (err) {
      onRestoreText(value);
      onError(err instanceof Error ? err.message : "Falha ao enviar");
    }
  }

  if (recording) {
    return <RecordingComposer onRecordingChange={onRecordingChange} />;
  }

  return (
    <footer className="wa-composer">
      <EmojiPicker
        open={activePopover === "emoji"}
        onToggle={() => togglePopover("emoji")}
        onClose={() => closePopover("emoji")}
        onSelectEmoji={insertEmoji}
      />
      <AttachmentMenu
        chatJid={chat.chatJid}
        open={activePopover === "attach"}
        onToggle={() => togglePopover("attach")}
        onClose={() => closePopover("attach")}
        onSent={refreshAfterSend}
        onError={onError}
        onSuccess={onSuccess}
      />
      <ShortcutPicker
        chat={chat}
        lead={lead}
        shortcuts={shortcuts}
        open={activePopover === "shortcut"}
        onToggle={() => togglePopover("shortcut")}
        onClose={() => closePopover("shortcut")}
        onLoadShortcuts={onLoadShortcuts}
        onApplyText={applyComposerText}
        onError={onError}
        onSuccess={onSuccess}
      />
      <ComposerTextInput
        textareaRef={textareaRef}
        chatJid={chat.chatJid}
        text={text}
        onTextChange={handleTextChange}
        onSubmit={() => void submitText()}
        onRecordingChange={onRecordingChange}
        onError={onError}
        onSuccess={onSuccess}
        onSent={refreshAfterSend}
      />
    </footer>
  );
}
